// react-client/src/features/feedback/pages/FeedbackSearchPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Alert,
  OutlinedInput,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Hooks
import { useFeedbackFilter } from '../hooks/useFeedbackFilter';
import { useFeedbackStatus } from '../hooks/useFeedbackManagement';
import { useDepartments } from '../../departments/hooks/useDepartment';
import { useStaff } from '../../staff/hooks/useStaff';
import { usePermissions } from '../../auth/hooks/usePermissions';

// Components
import FeedbackSearchTable from '../components/FeedbackSearchTable';
import PageHeader from '../../../components/common/PageHeader';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function FeedbackSearchPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['feedback', 'common']);
  const permissions = usePermissions();
  
  // Hooks
  const { feedbackItems, totalCount, loading, error, filterFeedback, clearError, clearResults } = useFeedbackFilter();
  const { VISIBILITY_STATUS, IMPLEMENTATION_STATUS } = useFeedbackStatus();
  const { departments, fetchAllDepartments } = useDepartments();
  const { staff, fetchDepartmentStaff } = useStaff();

  // Filter state
  const [filters, setFilters] = useState({
    department_ids: [],
    visibility_statuses: [],
    implementation_statuses: [],
    staff_member_ids: [],
    search_term: '',
  });

  // Check permissions
  const canModerate = permissions.hasScope('feedback:moderate');

  // Load departments and staff on mount
  useEffect(() => {
    if (canModerate) {
      fetchAllDepartments({ include_deactivated: false });
      fetchDepartmentStaff({ include_inactive: false });
    }
  }, [canModerate, fetchAllDepartments, fetchDepartmentStaff]);

  // Handle filter changes
  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle search
  const handleSearch = useCallback(() => {
    // Build filter object, excluding empty arrays and strings
    const searchFilters = {};
    
    if (filters.department_ids?.length > 0) {
      searchFilters.department_ids = filters.department_ids;
    }
    if (filters.visibility_statuses?.length > 0) {
      searchFilters.visibility_statuses = filters.visibility_statuses;
    }
    if (filters.implementation_statuses?.length > 0) {
      searchFilters.implementation_statuses = filters.implementation_statuses;
    }
    if (filters.staff_member_ids?.length > 0) {
      searchFilters.staff_member_ids = filters.staff_member_ids;
    }
    if (filters.search_term?.trim()) {
      searchFilters.search_term = filters.search_term.trim();
    }

    filterFeedback(searchFilters, { limit: 100, offset: 0 });
  }, [filters, filterFeedback]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setFilters({
      department_ids: [],
      visibility_statuses: [],
      implementation_statuses: [],
      staff_member_ids: [],
      search_term: '',
    });
    clearResults();
  }, [clearResults]);

  // Handle row click
  const handleRowClick = useCallback((feedback) => {
    navigate(`/feedback/${feedback.feedback_id}`);
  }, [navigate]);

  // Handle vote change - refresh the list
  const handleVoteChange = useCallback((feedbackId, voteData) => {
    // Refresh search results to get updated vote counts
    handleSearch();
  }, [handleSearch]);

  // Check if any filters are active
  const hasActiveFilters = 
    filters.department_ids?.length > 0 ||
    filters.visibility_statuses?.length > 0 ||
    filters.implementation_statuses?.length > 0 ||
    filters.staff_member_ids?.length > 0 ||
    filters.search_term?.trim();

  return (
    <Box>
      <PageHeader
        icon={FeedbackOutlinedIcon}
        title={t('feedback:search.title', 'Search Feedback')}
        subtitle={t('feedback:search.subtitle', 'Filter and search through feedback submissions')}
      />

      {/* Filter Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              {t('feedback:search.filters', 'Filters')}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {/* Search Term */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('feedback:search.search_label', 'Search Term')}
                placeholder={t('feedback:search.search_placeholder', 'Search in title, description, and admin response...')}
                value={filters.search_term}
                onChange={(e) => handleFilterChange('search_term', e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.disabled' }} />,
                }}
              />
            </Grid>

            {/* Visibility Status (Admin only) */}
            {canModerate && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="feedback-search-page-visibility-label">{t('feedback:search.visibility', 'Visibility Status')}</InputLabel>
                  <Select labelId="feedback-search-page-visibility-label"
                    multiple
                    value={filters.visibility_statuses}
                    onChange={(e) => handleFilterChange('visibility_statuses', e.target.value)}
                    input={<OutlinedInput label={t('feedback:search.visibility', 'Visibility Status')} />}
                    sx={{ minWidth: "250px" }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="NOT_DECIDED">
                      {t('feedback:status.visibility.not_decided', 'Not Decided')}
                    </MenuItem>
                    <MenuItem value="PUBLISHED">
                      {t('feedback:status.visibility.published', 'Published')}
                    </MenuItem>
                    <MenuItem value="HIDDEN">
                      {t('feedback:status.visibility.hidden', 'Hidden')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Implementation Status */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="feedback-search-page-status-label">{t('feedback:search.status', 'Implementation Status')}</InputLabel>
                <Select labelId="feedback-search-page-status-label"
                  multiple
                  value={filters.implementation_statuses}
                  onChange={(e) => handleFilterChange('implementation_statuses', e.target.value)}
                  input={<OutlinedInput label={t('feedback:search.status', 'Implementation Status')} />}
                  sx={{ minWidth: "250px" }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value.replace('_', ' ')} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="NOT_DECIDED">
                    {t('feedback:status.implementation.not_decided', 'Not Decided')}
                  </MenuItem>
                  <MenuItem value="PENDING_REVIEW">
                    {t('feedback:status.implementation.pending_review', 'Pending Review')}
                  </MenuItem>
                  <MenuItem value="IMPLEMENTED">
                    {t('feedback:status.implementation.implemented', 'Implemented')}
                  </MenuItem>
                  <MenuItem value="WILL_NOT_IMPLEMENT">
                    {t('feedback:status.implementation.will_not_implement', 'Will Not Implement')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Departments (Admin only) */}
            {canModerate && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="feedback-search-page-departments-label">{t('feedback:search.departments', 'Departments')}</InputLabel>
                  <Select labelId="feedback-search-page-departments-label"
                    multiple
                    value={filters.department_ids}
                    onChange={(e) => handleFilterChange('department_ids', e.target.value)}
                    input={<OutlinedInput label={t('feedback:search.departments', 'Departments')} />}
                    sx={{ minWidth: "250px" }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const dept = departments.find(d => d.department_id === value);
                          return (
                            <Chip key={value} label={dept?.name || value} size="small" />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.department_id} value={dept.department_id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Staff Members (Admin only) */}
            {canModerate && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="feedback-search-page-submitters-label">{t('feedback:search.submitters', 'Submitters')}</InputLabel>
                  <Select labelId="feedback-search-page-submitters-label"
                    multiple
                    value={filters.staff_member_ids}
                    onChange={(e) => handleFilterChange('staff_member_ids', e.target.value)}
                    input={<OutlinedInput label={t('feedback:search.submitters', 'Submitters')} />}
                    sx={{ minWidth: "250px" }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const member = staff.find(s => s.staff_member_id === value);
                          return (
                            <Chip key={value} label={member?.name || value} size="small" />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {staff.map((member) => (
                      <MenuItem key={member.staff_member_id} value={member.staff_member_id}>
                        {member.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {/* Action Buttons */}
          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              {t('feedback:search.search_button', 'Search')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={loading}
            >
              {t('feedback:search.clear_button', 'Clear Filters')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {feedbackItems.length > 0 && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                {t('feedback:search.results', 'Results')} ({totalCount})
              </Typography>
            </Box>
            
            <FeedbackSearchTable
              feedbackItems={feedbackItems}
              loading={loading}
              onRowClick={handleRowClick}
              onVoteChange={handleVoteChange}
              canModerate={canModerate}
            />
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && feedbackItems.length === 0 && hasActiveFilters && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('feedback:search.no_results', 'No results found')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('feedback:search.no_results_hint', 'Try adjusting your filters')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}